import { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Select, Input } from "antd";
import type { Option } from "../../types/generics";

// PROG-DETAIL identity flow — add/edit the exercise "identity" (Tipo +
// Gruppo Muscolare + Esercizio) on the Detail (visualizza) view, mirroring
// the cascading-catalog UX of the create/edit ExerciseCard form. For catalog
// exercises the chosen muscle group filters the catalog options and picking
// an option backfills muscleGroupId; for custom exercises the trainer types a
// free-text name and picks a muscle group freely.

export type ExerciseIdentity = {
  type: "catalog" | "custom";
  muscleGroupId?: number;
  exerciseId?: number;
  customName?: string;
};

type Props = {
  open: boolean;
  mode: "add" | "edit";
  initial?: {
    type: "catalog" | "custom";
    muscleGroupId?: number;
    exerciseId?: number;
    customName?: string;
  };
  // exercisesOptions[*].value is the full Exercise (so we can read
  // .muscleGroupId and .id off it), same shape as the create/edit form.
  exercisesOptions: Option[];
  muscleGroupOptions: Option[];
  onCancel: () => void;
  onConfirm: (identity: ExerciseIdentity) => void;
};

export default function ExerciseIdentityModal({
  open,
  mode,
  initial,
  exercisesOptions,
  muscleGroupOptions,
  onCancel,
  onConfirm,
}: Props) {
  const [type, setType] = useState<"catalog" | "custom">(
    initial?.type ?? "custom",
  );
  const [muscleGroupId, setMuscleGroupId] = useState<number | undefined>(
    initial?.muscleGroupId,
  );
  const [exerciseId, setExerciseId] = useState<number | undefined>(
    initial?.exerciseId,
  );
  const [customName, setCustomName] = useState<string>(
    initial?.customName ?? "",
  );

  // Reseed local state only on the open transition (false→true). Callers pass
  // an inline `initial` object (new reference each render), so depending on it
  // directly would wipe the user's in-progress selections on any parent
  // re-render while the modal is open.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setType(initial?.type ?? "custom");
      setMuscleGroupId(initial?.muscleGroupId);
      setExerciseId(initial?.exerciseId);
      setCustomName(initial?.customName ?? "");
    }
    wasOpen.current = open;
  }, [open, initial]);

  // When a muscle group is chosen (catalog), fetch the exercises for that group
  // via the API rather than client-filtering the preloaded list. Cached per
  // group so reselecting the same group doesn't refetch.
  const [groupExercises, setGroupExercises] = useState<Option[] | null>(null);
  const [loadingGroupEx, setLoadingGroupEx] = useState(false);
  const groupExCacheRef = useRef<Map<number, Option[]>>(new Map());

  useEffect(() => {
    if (!open || type !== "catalog" || muscleGroupId == null) {
      setGroupExercises(null);
      return;
    }
    const cached = groupExCacheRef.current.get(muscleGroupId);
    if (cached) {
      setGroupExercises(cached);
      return;
    }
    let active = true;
    setLoadingGroupEx(true);
    // Dynamic import keeps the axios-based API client out of this component's
    // static module graph (so pure-function tests of the view chain stay
    // transform-free).
    import("../../api/Entities")
      .then(({ Entities }) =>
        Entities.getExercises(
          new URLSearchParams({ muscleGroupId: String(muscleGroupId) }),
        ),
      )
      .then(({ data }) => {
        const opts: Option[] = (data.list ?? []).map((ex) => ({
          label: ex.name,
          value: ex,
        }));
        groupExCacheRef.current.set(muscleGroupId, opts);
        if (active) setGroupExercises(opts);
      })
      .catch(() => {
        if (active) setGroupExercises([]);
      })
      .finally(() => {
        if (active) setLoadingGroupEx(false);
      });
    return () => {
      active = false;
    };
  }, [open, type, muscleGroupId]);

  // Catalog options for the select: the API-fetched group exercises when a
  // muscle group is set, otherwise the full preloaded list. Mapped to
  // {label, value: exerciseId}.
  const filteredExerciseOptions = useMemo(() => {
    const source: Option[] =
      muscleGroupId != null ? groupExercises ?? [] : exercisesOptions;
    const base = source.map((opt) => ({
      label: opt.label,
      value: opt.value?.id as number,
    }));
    // Keep a labeled option for the currently-selected exercise even before the
    // group list has loaded (or if it isn't in that group) — otherwise the
    // Select falls back to rendering the raw id instead of the name. Resolve
    // the name from the full preloaded catalog.
    if (exerciseId != null && !base.some((o) => o.value === exerciseId)) {
      const fromFull = exercisesOptions.find((o) => o.value?.id === exerciseId);
      // Fall back to the seeded name (the card's resolved exercise name lives in
      // `customName`) so the Select shows the name even when the catalog list
      // isn't loaded / doesn't contain this exercise — never a raw "#id".
      const label =
        fromFull?.label ??
        fromFull?.value?.name ??
        (customName.trim() || `Esercizio #${exerciseId}`);
      return [{ label, value: exerciseId }, ...base];
    }
    return base;
  }, [exercisesOptions, groupExercises, muscleGroupId, exerciseId, customName]);

  const handleTypeChange = (next: "catalog" | "custom") => {
    setType(next);
    // Switching type clears the cross-type field to avoid stale values.
    if (next === "custom") {
      setExerciseId(undefined);
    } else {
      setCustomName("");
    }
  };

  const handleMuscleGroupChange = (value: number | undefined) => {
    setMuscleGroupId(value);
    // Changing the muscle group (catalog) invalidates the chosen exercise.
    if (type === "catalog") setExerciseId(undefined);
  };

  const handleExerciseChange = (value: number | undefined) => {
    setExerciseId(value);
    const picked =
      groupExercises?.find((opt) => opt.value?.id === value) ??
      exercisesOptions.find((opt) => opt.value?.id === value);
    const pickedMgId =
      picked?.value?.muscleGroupId ?? picked?.value?.muscleGroup?.id;
    if (pickedMgId != null) {
      setMuscleGroupId(pickedMgId);
    }
  };

  const isValid =
    type === "catalog"
      ? exerciseId != null
      : customName.trim().length > 0;

  const handleOk = () => {
    if (!isValid) return;
    if (type === "catalog") {
      onConfirm({
        type: "catalog",
        exerciseId,
        muscleGroupId,
      });
    } else {
      onConfirm({
        type: "custom",
        customName: customName.trim(),
        muscleGroupId,
      });
    }
  };

  return (
    <Modal
      title={mode === "add" ? "Aggiungi Esercizio" : "Modifica esercizio"}
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      okText={mode === "add" ? "Aggiungi" : "Salva"}
      cancelText="Annulla"
      okButtonProps={{ disabled: !isValid }}
      width={440}
      centered
      destroyOnClose
    >
      <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 16 }}>
        <div>
          <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
            Tipo
          </label>
          <Select
            size="large"
            className="w-100 custom-input"
            value={type}
            onChange={handleTypeChange}
            options={[
              { label: "Catalogo", value: "catalog" },
              { label: "Personalizzato", value: "custom" },
            ]}
          />
        </div>

        <div>
          <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
            Gruppo Muscolare
          </label>
          <Select
            size="large"
            className="w-100 custom-input"
            showSearch
            allowClear
            placeholder="Seleziona un gruppo muscolare..."
            value={muscleGroupId ?? undefined}
            onChange={(v) => handleMuscleGroupChange(v ?? undefined)}
            options={muscleGroupOptions}
            filterOption={(input, option) =>
              (option?.label ?? "")
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        </div>

        {type === "catalog" ? (
          <div>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
              Esercizio
            </label>
            <Select
              size="large"
              className="w-100 custom-input"
              showSearch
              allowClear
              placeholder="Seleziona un esercizio..."
              value={exerciseId ?? undefined}
              onChange={(v) => handleExerciseChange(v ?? undefined)}
              loading={loadingGroupEx}
              options={filteredExerciseOptions}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </div>
        ) : (
          <div>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
              Nome esercizio
            </label>
            <Input
              size="large"
              className="custom-input"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder="Nome esercizio..."
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
